import React, { useEffect, useState } from 'react';
import * as PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import moment from 'moment';
import 'moment/locale/en-gb';
import {
  Button, Table, Typography, Row,
} from 'antd';
import { connect } from 'react-redux';
import { selectBuckets } from '../../../../state/domain/buckets/bucketReducer';
import {
  editBucket, requestAllBuckets,
} from '../../../../state/ui/credits/bucketUiActions';
import {DATE_FORMAT, DATE_FORMAT_SHORTER} from '../packageUtil';
import ConfigHeading from '../../configure/components/ConfigHeading';
import '../bynx.css';
import {selectFetchBucketsRequest} from "../../../../state/ui/credits/bucketUiReducer";
import {userIsConsoleFinance} from "../../configure/company/user-util";
import {selectSessionUser} from "../../../../state/ui/application/sessionUiReducer";
import EditCreditBucket from "./EditCreditBucket";
import {getDefaultLocale} from "../../../../configWeb";

const { Column } = Table;
const { Text } = Typography;

// TABLE
function BucketsTable({
  isLoading, visibleBuckets, locale, //onEditButton
}) {
  const RowTotal = () => (
    <div>
      <Text>{`${visibleBuckets.length} `}</Text>
      <FormattedMessage id="items" />
    </div>
  );

  function sortByDescription(a, b) {
    const aStr = `${a.description}`;
    const bStr = `${b.description}`;
    return aStr.localeCompare(bStr);
  }

  function sortByFromDate(a, b) {
    const adate = moment.utc(a.dateFrom, DATE_FORMAT);
    const bdate = moment.utc(b.dateFrom, DATE_FORMAT);
    if (adate.isBefore(bdate)) {
      return 1;
    }
    if (adate.isAfter(bdate)) {
      return -1;
    }
    return 0;
  }

  function sortByExpiryDate(a, b) {
    const adate = moment.utc(a.dateExpiry, DATE_FORMAT);
    const bdate = moment.utc(b.dateExpiry, DATE_FORMAT);
    if (adate.isBefore(bdate)) {
      return 1;
    }
    if (adate.isAfter(bdate)) {
      return -1;
    }
    return 0;
  }

  return (
    <Table
      pagination={{ defaultPageSize: 10, showTotal: () => <RowTotal /> }}
      scroll={{ x: 1, y: false }}
      loading={isLoading}
      dataSource={visibleBuckets}
      rowKey={record => record.id}
    >
      <Column
          title={<FormattedMessage id="idText" />}
          dataIndex="id"
          key="id"
      />
      <Column
        sorter={sortByDescription}
        title={<FormattedMessage id="description" />}
        dataIndex="description"
        key="description"
      />
      <Column
          title={<FormattedMessage id="creditsAllocated" />}
          dataIndex="tokensAllocated"
          key="tokensAllocated"
          align="right"
          render={(number) => { return (number ? number.toLocaleString(locale) : 0); }}
      />
      <Column
        sorter={sortByFromDate}
        defaultSortOrder="ascend"
        title={<FormattedMessage id="startDateUTC" />}
        dataIndex="dateFrom"
        key="dateFrom"
      />
      <Column
          sorter={sortByExpiryDate}
          title={<FormattedMessage id="expiryDate" />}
          dataIndex="dateExpiry"
          key="dateExpiry"
      />
      <Column
          title={<FormattedMessage id="createdBy" />}
          dataIndex="createdBy"
          key="createdBy"
          render={(active, record) => (
              <Row type="flex" justify="start">
                <Text>{record.createdUsername}</Text>&nbsp;<FormattedMessage id="on" />&nbsp;<Text>{record.createdDate}</Text>
              </Row>
          )}
      />
      {/*<Column*/}
      {/*  className="via-action-column"*/}
      {/*  align="right"*/}
      {/*  width="70px"*/}
      {/*  render={(text, record) => {*/}
      {/*    const startDate = moment.utc(record.packageStartDate, DATE_FORMAT);*/}
      {/*    const editable = !record.active || moment.utc().isBefore(startDate);*/}
      {/*    return (*/}
      {/*      <Row>*/}
      {/*        <Button size="small" type="primary" onClick={() => onEditButton(record)} icon="edit">*/}
      {/*          <FormattedMessage id={editable ? 'edit' : 'view'} />*/}
      {/*        </Button>*/}
      {/*      </Row>*/}
      {/*    );*/}
      {/*  }}*/}
      {/*/>*/}
    </Table>
  );
}

BucketsTable.propTypes = {
  isLoading: PropTypes.bool,
  onEditButton: PropTypes.func.isRequired,
  visibleBuckets: PropTypes.arrayOf(
    PropTypes.shape({
        id: PropTypes.number.isRequired,
        description: PropTypes.string.isRequired,
        dateFrom: PropTypes.string.isRequired,
        dateExpiry: PropTypes.string.isRequired,
        tokensAllocated: PropTypes.number.isRequired,
        createdBy: PropTypes.number,
        createdDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        createdUsername: PropTypes.string,
    }),
  ),
};

BucketsTable.defaultProps = {
  isLoading: false,
  visibleBuckets: [],
};

function CreditBuckets({
  isLoading, loadPageData, buckets, onCreateOrEdit, sessionUser,
}) {
  // Manage company - hard-coded for now
  const [manageCompany] = useState('PURCO');

  // Load users on first entry - these only need to be loaded once.
  useEffect(() => {
    loadPageData(manageCompany);
  }, [loadPageData, manageCompany]);

  const intl = useIntl();

  const [visibleBuckets, setVisibleBuckets] = useState([]);
  useEffect(() => {
    const tempBuckets = buckets.slice();
    setVisibleBuckets(tempBuckets.map(strBucket => ({
      ...strBucket,
      dateFrom: moment.utc(strBucket.dateFrom).format(DATE_FORMAT),
      dateExpiry: moment.utc(strBucket.dateExpiry).format(DATE_FORMAT),
      createdDate: moment.utc(strBucket.createdDate).format(DATE_FORMAT_SHORTER),
    })));
  }, [buckets]);

  const CreateButton = () => (
    <Button type="primary" onClick={() => onCreateOrEdit(null)} loading={isLoading} icon="plus">
      <FormattedMessage id="addCredit" />
    </Button>
  );

  // Locale adjust
  const userLocale = sessionUser && sessionUser.locale ? sessionUser.locale.replace('_', '-') : getDefaultLocale().locale;

  const startDateDupeCheck = (code, startDate) => !visibleBuckets.find(
    batch => (batch.id !== code) && moment.utc(batch.dateFrom, DATE_FORMAT).isSame(startDate),
  );

  return (
    <ConfigHeading
      titleId="manageCompanyCredits"
      descriptionId="manageCompanyCreditsBlurb"
      parameters={
        <div>
          <Text style={{ fontWeight: 'bold' }}><FormattedMessage id="manageCompany" />: </Text>
          <Text>PurCo Fleet Services Inc.</Text>
        </div>
      }
      actions={
        userIsConsoleFinance(sessionUser)
            ? <CreateButton/>
            : <></>
      }
    >
      <BucketsTable
        isLoading={isLoading}
        visibleBuckets={visibleBuckets}
        onEditButton={onCreateOrEdit}
        intl={intl}
        locale={userLocale}
      />
      <EditCreditBucket manageCompany={manageCompany} startDateDupeCheck={startDateDupeCheck} sessionUser={sessionUser} />
    </ConfigHeading>
  );
}

// Validate the prop type parameters.
CreditBuckets.propTypes = {
  isLoading: PropTypes.bool,
  loadPageData: PropTypes.func.isRequired,
  buckets: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    description: PropTypes.string.isRequired,
    dateFrom: PropTypes.string.isRequired,
    dateExpiry: PropTypes.string.isRequired,
    tokensAllocated: PropTypes.number.isRequired,
    createdBy: PropTypes.number,
    createdDate: PropTypes.oneOfType([PropTypes.string,PropTypes.object]),
    createdUsername: PropTypes.string,
  })),
  onCreateOrEdit: PropTypes.func.isRequired,
};

CreditBuckets.defaultProps = {
  isLoading: false,
  buckets: [],
};

/**
 * A function for connecting redux state to the component props.
 */
const mapStateToProps = (state) => {
  const pageDataRequest = selectFetchBucketsRequest(state);

  return {
    isLoading: pageDataRequest ? pageDataRequest.isLoading : false,
    error: pageDataRequest ? pageDataRequest.error : null,
    buckets: selectBuckets(state),
    sessionUser: selectSessionUser(state),
  };
};

/**
 * A function for connecting redux action to the component props.
 */
const mapDispatchToProps = dispatch => ({
  loadPageData: (manageCompany) => dispatch(requestAllBuckets(manageCompany)),
  onCreateOrEdit: batch => dispatch(editBucket(batch ? batch.id : null)),
});

/**
 * Connect the redux state to our component.
 */
export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(CreditBuckets));
